"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import Link from "next/link";
import { CalendarDays, CheckCircle, Users, GitGraph } from "lucide-react";

export default function HomePageContent() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-neutral-100 text-neutral-900">
      <div className="container mx-auto px-6 py-20">
        <div className="flex flex-col lg:flex-row items-center">
          <div className="lg:w-1/2 mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold mb-8 leading-tight text-neutral-900">
              Transform Your Practice with Guide
            </h1>
            <p className="text-xl mb-8 text-neutral-600">
              Streamline your sessions, manage tasks, and track progress all in
              one place. Built for professionals who care about delivering
              results.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white/80 backdrop-blur-lg py-20 border-y border-neutral-200">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16 text-neutral-900">
            Why Choose Guide?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: (
                  <CalendarDays className="w-12 h-12 mb-4 text-neutral-700" />
                ),
                title: "Smart Scheduling",
                description:
                  "Effortlessly manage your appointments and sessions",
              },
              {
                icon: (
                  <CheckCircle className="w-12 h-12 mb-4 text-neutral-700" />
                ),
                title: "Task Management",
                description: "Track progress and assignments with ease",
              },
              {
                icon: <GitGraph className="w-12 h-12 mb-4 text-neutral-700" />,
                title: "Progress Tracking",
                description: "Track client progress via their tasks",
              },
              {
                icon: <Users className="w-12 h-12 mb-4 text-neutral-700" />,
                title: "Client Portal",
                description:
                  "Give clients access to their personalized dashboard",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-lg bg-neutral-100 hover:bg-neutral-200 transition-all "
              >
                {feature.icon}
                <h3 className="text-xl font-semibold mb-2 text-neutral-900">
                  {feature.title}
                </h3>
                <p className="text-neutral-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Registration Cards */}
      <div className="container mx-auto px-6 py-20">
        <h2 className="text-4xl font-bold text-center mb-16 text-neutral-900">
          Get Started Today
        </h2>
        <div className="flex flex-col md:flex-row gap-8 max-w-4xl mx-auto">
          <Card className="flex-1 bg-white border border-neutral-200">
            <CardContent className="p-8">
              <CardTitle className="text-2xl font-bold mb-4 text-neutral-900">
                For Clients
              </CardTitle>
              <ul className="space-y-4 mb-8 text-neutral-600">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>Personal Progress Dashboard</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>Task Management</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>Session Scheduling</span>
                </li>
              </ul>
              <Link href="/student/register">
                <Button className="w-full">Register as Client</Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="flex-1 bg-black text-white border-0">
            <CardContent className="p-8">
              <CardTitle className="text-2xl font-bold mb-4">
                For Professionals
              </CardTitle>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>Client Management</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>Schedule Organization</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>Progress Tracking Tools</span>
                </li>
              </ul>
              <Link href="/coach/register">
                <Button className="w-full bg-white text-black hover:bg-neutral-100">
                  Register as Professional
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      <footer className="bg-neutral-50 border-t border-neutral-200 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-neutral-600">
              &copy; {new Date().getFullYear()} SmartAlgorhythm. All rights
              reserved.
            </p>
            <div className="flex gap-8 mt-4 md:mt-0">
              <Link
                href="https://www.smartalgorhythm.com/privacy-policy"
                className="text-neutral-600 hover:text-neutral-900"
              >
                Privacy Policy
              </Link>
              <Link
                href="https://www.smartalgorhythm.com/terms-and-conditions"
                className="text-neutral-600 hover:text-neutral-900"
              >
                Terms of Service
              </Link>
              <Link
                href="/contact"
                className="text-neutral-600 hover:text-neutral-900"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
